import { useState } from "react";
import { useQuery, gql } from "@apollo/client";
import { useParams } from "react-router-dom";
import { HiUsers } from "react-icons/hi2";
import { MissionType, type GetContractDetailQuery, type GetContractDetailQueryVariables } from "../../../../__generated__/graphql";

const GET_CONTRACT_DETAIL = gql`
  query GetContractDetail($contractId: Int!) {
    contract(contractId: $contractId) {
      id
      title
      totalBudget
      startAt
      endAt
      status
      targetRevenue
      missionTargets {
        id
        missionType
        targetCount
      }
      campaigns {
        id
        productName
        productImageUrl
        channel
        originalPrice
        discountPrice
        paybackPrice
        runs {
          id
          startAt
          endAt
          isActive
          participationCount
          participations {
            id
            userId
            status
            joinedAt
            user {
              id
              uuid
              name
              phone
              area1
              area2
              area3
              area4
            }
            submissions {
              id
              stepOrder
              status
              isSubmitted
              isApproved
              proofData
              step {
                id
                stepOrder
                type
              }
            }
          }
        }
        steps {
          id
          stepOrder
          type
          title
        }
      }
    }
  }
`;

const extractImageUrls = (proofData: unknown): string[] => {
  if (!proofData) return [];

  // 1) 문자열이면 JSON 파싱 시도 → 실패하면 URL 문자열로 간주
  if (typeof proofData === "string") {
    try {
      const parsed = JSON.parse(proofData);
      return extractImageUrls(parsed);
    } catch {
      return [proofData];
    }
  }

  // 2) 배열이면 재귀적으로 펼치기
  if (Array.isArray(proofData)) {
    return proofData.flatMap((item) => extractImageUrls(item));
  }

  // 3) 객체면 흔한 키들에서 URL 찾기
  if (typeof proofData === "object") {
    const obj = proofData as Record<string, unknown>;

    const candidates: unknown[] = [
      obj.imageUrl,
      obj.imageUrls,
      obj.images,
      obj.url,
      obj.urls,
    ];

    return candidates.flatMap((c) => extractImageUrls(c));
  }

  return [];
};

const ContractDetail = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const [activeTab, setActiveTab] = useState<"campaigns" | "certifications" | "status">("campaigns");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const { data, loading, error } = useQuery<GetContractDetailQuery, GetContractDetailQueryVariables>(GET_CONTRACT_DETAIL, {
    variables: { contractId: parseInt(contractId || "0") },
    skip: !contractId,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.contract) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500">에러가 발생했습니다: {error?.message || "컨트랙트를 찾을 수 없습니다."}</div>
      </div>
    );
  }

  const contract = data.contract;
  const campaigns = contract.campaigns ?? [];
  const missionTargets = contract.missionTargets ?? [];

  const MISSION_TYPE_ORDER = [
    MissionType.Search,
    MissionType.Like,
    MissionType.Purchase,
    MissionType.Review,
    MissionType.PhotoReview,
    MissionType.UsageShot,
  ] as const;
  type VisibleMissionType = (typeof MISSION_TYPE_ORDER)[number];

  const MISSION_TYPE_LABELS: Record<VisibleMissionType, string> = {
    [MissionType.Search]: "검색",
    [MissionType.Like]: "플레이스 저장 & 알림받기",
    [MissionType.Purchase]: "플레이스 내 구매",
    [MissionType.Review]: "리뷰",
    [MissionType.PhotoReview]: "실 포토리뷰",
    [MissionType.UsageShot]: "사용샷",
  };

  const isVisibleMissionType = (value: MissionType): value is VisibleMissionType => {
    return (MISSION_TYPE_ORDER as readonly MissionType[]).includes(value);
  };

  const missionTargetCountByType = new Map<VisibleMissionType, number>();
  missionTargets.forEach((t) => {
    if (!isVisibleMissionType(t.missionType)) return;
    missionTargetCountByType.set(t.missionType, t.targetCount);
  });

  // 컨트랙트에 있는 미션 타입만 노출 (지정한 타입만)
  const visibleMissionTypes = MISSION_TYPE_ORDER.filter((type) => missionTargetCountByType.has(type));
  const visibleMissionTypeSet = new Set<VisibleMissionType>(visibleMissionTypes);

  // 모든 참여자 수집
  const allParticipations: typeof campaigns[number]["runs"][number]["participations"][number][] = [];
  campaigns.forEach((campaign) => {
    campaign.runs?.forEach((run) => {
      if (run.participations?.length) {
        allParticipations.push(...run.participations);
      }
    });
  });

  // 챌린지 인증 현황용: 미션 타입별 승인된 "사람" 수 (userId 기준으로 중복 제거)
  const approvedUserIdsByMissionType = new Map<VisibleMissionType, Set<number>>();
  visibleMissionTypes.forEach((type) => approvedUserIdsByMissionType.set(type, new Set<number>()));

  allParticipations.forEach((participation) => {
    const userId = participation.userId;

    participation.submissions.forEach((s) => {
      if (!s.isApproved) return;
      const type = s.step?.type;
      if (!type) return;
      if (!isVisibleMissionType(type)) return;
      if (!visibleMissionTypeSet.has(type)) return;
      approvedUserIdsByMissionType.get(type)!.add(userId);
    });
  });

  const challengeCertificationStats = visibleMissionTypes.map((type) => {
    const completed = approvedUserIdsByMissionType.get(type)?.size ?? 0;
    const total = missionTargetCountByType.get(type) ?? 0;
    return {
      type,
      label: MISSION_TYPE_LABELS[type],
      completed,
      total,
      percentage: total > 0 ? ((completed / total) * 100).toFixed(1) : "0.0",
    };
  });

  // 구매 확정 (PURCHASE + APPROVED - 승인된 submission 카운트)
  let purchaseConfirmedCount = 0;
  allParticipations.forEach((participation) => {
    if (!Array.isArray(participation.submissions)) return;
    const hasPurchaseApproved = participation.submissions.some(
      (s) => s.step?.type === MissionType.Purchase && s.isApproved
    );
    if (hasPurchaseApproved) {
      purchaseConfirmedCount++;
    }
  });

  // 예상 매출 계산 (구매 확정된 사람 수 * 1인당 구매가)
  // 각 참여자가 구매한 캠페인의 paybackPrice를 합산
  let totalExpectedSales = 0;
  let purchaserCount = purchaseConfirmedCount;
  
  // 구매 확정된 참여자들의 paybackPrice 합산
  allParticipations.forEach((participation) => {
    if (!Array.isArray(participation.submissions)) return;
    const purchaseSubmission = participation.submissions.find(
      (s) => s.step?.type === MissionType.Purchase && s.isApproved
    );
    if (!purchaseSubmission) return;

    // 해당 참여자의 run에서 campaign의 price 찾기
    campaigns.forEach((campaign) => {
      campaign.runs?.forEach((run) => {
        if (run.participations?.some((p) => p.id === participation.id)) {
          totalExpectedSales += campaign.originalPrice || 0;
        }
      });
    });
  });

  const averagePricePerPerson = purchaserCount > 0 ? totalExpectedSales / purchaserCount : 0;

  // 컨트랙트 상태 확인
  const now = new Date();
  const startAt = new Date(contract.startAt);
  const endAt = new Date(contract.endAt);
  const isActive = now >= startAt && now <= endAt && contract.status === "ACTIVE";

  // 진행률 계산 (현재 날짜 기준)
  const contractDuration = endAt.getTime() - startAt.getTime();
  const elapsedTime = now.getTime() - startAt.getTime();
  const progressPercentage = contractDuration > 0
    ? Math.min(100, Math.max(0, (elapsedTime / contractDuration) * 100))
    : 0;

  // 날짜 포맷 함수
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  return (
    <div>
      {/* 탭 메뉴 */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("campaigns")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "campaigns"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          캠페인
        </button>
        <button
          onClick={() => setActiveTab("certifications")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "certifications"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          인증샷/후기
        </button>
        <button
          onClick={() => setActiveTab("status")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "status"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          현황
        </button>
      </div>

      {/* 현황 탭 컨텐츠 */}
      {activeTab === "status" && (
        <div className="space-y-6">
          {/* 챌린지 예상 매출 */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-bold text-gray-900">챌린지 예상 매출</h2>
              {isActive && (
                <span className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                  진행중
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-end mb-8">
              <div>
                <div className="text-sm font-medium text-gray-500 mb-3">총 예상 매출</div>
                <div className="text-5xl font-black text-gray-900 tracking-tight">
                  {Math.round(totalExpectedSales).toLocaleString()}<span className="text-2xl font-bold ml-1">원</span>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-3">1인당 평균 구매가</div>
                <div className="text-3xl font-bold text-gray-900 tracking-tight">
                  {Math.round(averagePricePerPerson).toLocaleString()}<span className="text-xl font-bold ml-1">원</span>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-3">현재 구매 완료 인원</div>
                <div className="text-3xl font-bold text-gray-900 tracking-tight">
                  {purchaserCount}<span className="text-xl font-bold ml-1">명</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 flex items-center gap-1.5">
                <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                캠페인 종료 후 인증샷 검토 결과에 따라 매출이 변동될 수 있습니다.
              </p>
            </div>
          </div>

          {/* 챌린지 인증 현황 */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-10">챌린지 인증 현황</h2>
            {challengeCertificationStats.length === 0 ? (
              <div className="text-gray-400 text-center py-10 italic">설정된 미션 목표가 없습니다.</div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                {challengeCertificationStats.map((stat) => (
                  <div key={stat.type} className="flex flex-col items-center text-center">
                    <div className="text-sm font-medium text-gray-500 mb-6">{stat.label}</div>
                    <div className="text-5xl md:text-6xl font-black text-gray-900 mb-4 tracking-tight">
                      {stat.percentage}%
                    </div>
                    <div className="flex items-center justify-center gap-1 text-sm font-medium text-gray-400">
                      <HiUsers className="w-4 h-4" />
                      <span>{stat.completed}명/{stat.total}명</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 캠페인 탭 컨텐츠 */}
      {activeTab === "campaigns" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">{contract.title}</h2>
            
            {/* 첫 번째 행: 컨트랙트 기간 | 목표 매출 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* 컨트랙트 기간 및 진행률 */}
              <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
                <h3 className="text-sm font-bold text-gray-400 mb-4 uppercase tracking-wider">컨트랙트 기간</h3>
                <div className="text-lg text-gray-900 font-bold mb-6">
                  {formatDate(contract.startAt)} ~ {formatDate(contract.endAt)}
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-500">전체 진행률</span>
                    <span className="text-2xl font-black text-blue-600 tracking-tighter">
                      {progressPercentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all duration-500 shadow-sm"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* 목표 매출 */}
              <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm flex flex-col justify-center">
                <h3 className="text-sm font-bold text-gray-400 mb-4 uppercase tracking-wider">총 목표 매출</h3>
                <div className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
                  {contract.targetRevenue != null
                    ? contract.targetRevenue.toLocaleString()
                    : "-"}<span className="text-2xl font-bold ml-1 text-gray-500">원</span>
                </div>
                {contract.targetRevenue == null && (
                  <div className="text-sm text-gray-400 mt-2 italic">목표 매출이 설정되지 않았습니다.</div>
                )}
              </div>
            </div>

            {/* 두 번째 행: 목표 (전체 너비) */}
            <div className="p-8 border border-gray-200 rounded-lg bg-white shadow-sm">
              <h3 className="text-sm font-bold text-gray-400 mb-10 uppercase tracking-wider text-left">세부 목표 현황</h3>
              {contract.missionTargets && contract.missionTargets.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
                  {contract.missionTargets.map((target) => {
                    // 미션 타입을 한글로 변환
                    const getMissionTypeLabel = (type: string) => {
                      const labels: Record<string, string> = {
                        SEARCH: "검색",
                        LIKE: "찜",
                        ADD_TO_CART: "장바구니",
                        PURCHASE: "구매",
                        REVIEW: "리뷰",
                        PHOTO_REVIEW: "포토리뷰",
                        USAGE_SHOT: "사용샷 인증",
                        ETC: "기타",
                      };
                      return labels[type] || type;
                    };

                    return (
                      <div key={target.id} className="flex flex-col items-start text-left">
                        <div className="text-xs font-bold text-gray-400 mb-4 uppercase tracking-widest">
                          {getMissionTypeLabel(target.missionType)}
                        </div>
                        <div className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
                          {target.targetCount.toLocaleString()}
                          <span className="text-lg font-bold ml-0.5 text-gray-500">회</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-gray-400 text-center py-4 italic">설정된 목표가 없습니다.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 인증샷/후기 탭 */}
      {activeTab === "certifications" && (
        <div className="space-y-6">
          {campaigns.map((campaign) => {
            const campaignRuns = campaign.runs ?? [];
            const allCampaignParticipations = campaignRuns.flatMap((run) => run.participations ?? []);
            type CampaignParticipation = (typeof allCampaignParticipations)[number];
            const activeRunCount = campaignRuns.filter((r) => r.isActive).length;
            const totalParticipantsInRuns = campaignRuns.reduce(
              (sum, run) => sum + (run.participationCount || 0),
              0
            );
            const firstRunStartAt =
              campaignRuns.length > 0
                ? campaignRuns.reduce((min, r) =>
                    new Date(r.startAt).getTime() < new Date(min).getTime() ? r.startAt : min
                  , campaignRuns[0].startAt)
                : null;
            const lastRunEndAt =
              campaignRuns.length > 0
                ? campaignRuns.reduce((max, r) =>
                    new Date(r.endAt).getTime() > new Date(max).getTime() ? r.endAt : max
                  , campaignRuns[0].endAt)
                : null;

            // 유저별로 그룹핑 (같은 유저가 여러 run에 참여할 수 있음)
            const userMap = new Map<
              number,
              { user: CampaignParticipation["user"]; participations: CampaignParticipation[] }
            >();
            allCampaignParticipations.forEach((participation) => {
              const userId = participation.userId;
              if (!userMap.has(userId)) {
                userMap.set(userId, {
                  user: participation.user,
                  participations: [],
                });
              }
              userMap.get(userId)!.participations.push(participation);
            });

            const users = Array.from(userMap.values());
            const steps = campaign.steps ?? [];
            const sortedSteps = [...steps].sort((a, b) => a.stepOrder - b.stepOrder);

            // 주소 포맷 함수
            const formatAddress = (user?: CampaignParticipation["user"] | null) => {
              if (!user) return "-";
              const parts = [user.area1, user.area2, user.area3, user.area4].filter(Boolean);
              return parts.length > 0 ? parts.join(" ") : "-";
            };

            // 인증 상태 렌더링 함수
            const renderCertificationStatus = (
              participation: CampaignParticipation,
              step: (typeof sortedSteps)[number]
            ) => {
              const submission = participation.submissions.find(
                (s) => s.step?.id === step.id
              );

              if (!submission) {
                return (
                  <td key={step.id} className="px-4 py-3 text-center">
                    <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400 mx-auto">
                      -
                    </div>
                  </td>
                );
              }

              const proofData = (submission as { proofData?: unknown | null }).proofData;
              const proofImageUrl = extractImageUrls(proofData)[0];

              // 인증 성공 (isApproved가 true인 경우)
              if (submission.isApproved) {
                return (
                  <td key={step.id} className="px-4 py-3 text-center">
                    <div
                      className={`w-20 h-20 rounded relative mx-auto overflow-hidden ${
                        proofImageUrl ? "cursor-zoom-in" : ""
                      }`}
                      onClick={() => proofImageUrl && setSelectedImage(proofImageUrl)}
                    >
                      {/* 기본 배경 (이미지 로드 실패 시에도 노출) */}
                      <div className="absolute inset-0 bg-gray-200" />

                      {proofImageUrl && (
                        <img
                          src={proofImageUrl}
                          alt="인증 이미지"
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => {
                            // 이미지 로드 실패 시 이미지를 숨기고(배경 유지)
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}

                      {/* 검은색 마스킹(반투명) */}
                      <div className="absolute inset-0 bg-black/45 pointer-events-none" />

                      {/* 인증 성공 체크 - 가운데 정렬 + 라운드 배경 */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-9 h-9 rounded-full bg-white/85 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-6 h-6 text-green-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </td>
                );
              }

              // 제출됨 (isSubmitted가 true이지만 아직 승인되지 않은 경우)
              if (submission.isSubmitted) {
                return (
                  <td key={step.id} className="px-4 py-3 text-center">
                    <div
                      className={`w-20 h-20 rounded relative mx-auto overflow-hidden ${
                        proofImageUrl ? "cursor-zoom-in" : ""
                      }`}
                      onClick={() => proofImageUrl && setSelectedImage(proofImageUrl)}
                    >
                      {/* 기본 배경 (이미지 로드 실패 시에도 노출) */}
                      <div className="absolute inset-0 bg-gray-200" />

                      {proofImageUrl && (
                        <img
                          src={proofImageUrl}
                          alt="인증 이미지"
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}

                      <div className="absolute inset-0 bg-black/30 pointer-events-none" />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="px-2 py-1 rounded-full bg-white/85 text-gray-800 text-xs font-semibold">
                          대기중
                        </span>
                      </div>
                    </div>
                  </td>
                );
              }

              // 미제출
              return (
                <td key={step.id} className="px-4 py-3 text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400 mx-auto">
                    -
                  </div>
                </td>
              );
            };

            return (
              <div key={campaign.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* 캠페인 헤더 */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-start gap-4">
                    {campaign.productImageUrl && (
                      <img
                        src={campaign.productImageUrl}
                        alt={campaign.productName}
                        className="w-20 h-20 object-cover rounded-xl bg-gray-100"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-semibold text-gray-900">
                        {campaign.productName}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {campaign.channel}
                      </p>

                      <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-gray-600">
                        {firstRunStartAt && lastRunEndAt && (
                          <span className="px-2.5 py-1 rounded-full bg-white border border-gray-200">
                            기간 {formatDate(firstRunStartAt)} ~ {formatDate(lastRunEndAt)}
                          </span>
                        )}
                        <span className="px-2.5 py-1 rounded-full bg-white border border-gray-200">
                          회차 {activeRunCount}회
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-white border border-gray-200">
                          참여 {totalParticipantsInRuns.toLocaleString()}명
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-white border border-gray-200">
                          원가 {campaign.originalPrice.toLocaleString()}원
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-white border border-gray-200">
                          할인가 {campaign.discountPrice.toLocaleString()}원
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-white border border-gray-200">
                          페이백 {campaign.paybackPrice.toLocaleString()}원
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 테이블 */}
                {users.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    참여자가 없습니다.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            UUID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            이름
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            휴대폰 번호
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            사는곳
                          </th>
                          {sortedSteps.map((step) => (
                            <th
                              key={step.id}
                              className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              {step.title || `인증단계 ${step.stepOrder}`}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((userGroup) => {
                          // 가장 최근 participation 사용 (여러 run에 참여한 경우)
                          const participation = userGroup.participations[userGroup.participations.length - 1];
                          const user = userGroup.user;

                          return (
                            <tr key={user.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.uuid || `-`}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.name || `유저 ${user.id}`}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {user.phone || "-"}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {formatAddress(user)}
                                </div>
                              </td>
                              {sortedSteps.map((step) =>
                                renderCertificationStatus(participation, step)
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {campaigns.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-center py-8 text-gray-500">
                캠페인이 없습니다.
              </div>
            </div>
          )}
        </div>
      )}

      {/* 인증 이미지 확대 모달 */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-all z-10"
              aria-label="닫기"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src={selectedImage}
              alt="확대 이미지"
              className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg mx-auto"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractDetail;
